// src/screens/HouseholdChecklist/HouseholdChecklist.tsx
// -----------------------------------------------------------------------------
// Container component: pulls household data, derives tasks, renders progress
// bar, list, and context-menu.
// -----------------------------------------------------------------------------

import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import Menu from '@mui/material/Menu';
import MenuItem, { menuItemClasses } from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';

import ArrowOutwardOutlinedIcon from '@mui/icons-material/ArrowOutwardOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';

import ChecklistRow from './household-checklist-row';
import useHousehold from "src/contexts/household-context";
import type { Task, Household, UserDataObj } from "src/types/snugtotal";
import { paths } from "src/paths";
import { toast } from 'react-hot-toast';
import { GenericPath, useV3DocumentDownloadAll } from 'src/api/snugtotal/estate_app';
import { EstateDocumentType, MinimalEstateDocument } from 'src/types/snugdocs';
import { useEstateDocumentStore } from 'src/hooks/snug/use-estateDocs';
import { useRouter } from 'src/hooks/use-router';
import CircularProgress from '@mui/material/CircularProgress';
import useUserDataProvider  from 'src/contexts/userData-context';

/* =============================================================================
   Inline helpers
============================================================================= */

/** narrow filter: remove null | undefined */
const notEmpty = <T,>(v: T | null | undefined): v is T => v != null;
/** docs -> has at least one with status === APPROVED */
const hasApproved = (docs?: any[]) => docs?.some((d) => (d?.status === 'APPROVED' || d?.status === 'ANALYSIS_PENDING' || d?.status === 'ANALYSIS_COMPLETE'));

// ---------------- helpers ----------------------------------------------------
type PersonRole = 'HOH' | 'SPOUSE' | 'CHILD' | 'OTHER';
type TaskPlusRole = Task & { primaryRole: PersonRole };

const roleOf = (
  user: UserDataObj,
  hoh: UserDataObj,
  spouse?: UserDataObj | null
): PersonRole => {
  if (user.ud_id === hoh.ud_id) return 'HOH';
  if (spouse && user.ud_id === spouse.ud_id) return 'SPOUSE';
  // if your member object carries a role field you can refine this:
  return 'CHILD';                     // or 'OTHER'
};

// ---------------- buildTasks -------------------------------------------------
const buildTasks = (hh?: Household | null, userData?: UserDataObj | null): TaskPlusRole[] => {
  if (!hh || !hh.head_of_household) return [];

  const hoh = hh.head_of_household;
  const members = hh.household_member_roles ?? [];
  const spouse = members.find(m => m.role === 'SPOUSE')?.role_target_user_data ?? null;

  const epf = hoh.estate_plan_foundation as
    | 'joint' | 'trust' | 'will' | 'attorney' | 'skip' | '' | null;

  const tasks: TaskPlusRole[] = [];


  // ---- 1. Will / Trust rows -----------------------------------------------
  if (epf === 'joint') {
    tasks.push({
      id: 'jointTrust',
      kind: 'lwt',
      title: 'Joint Revocable Living Trust',
      people: [hoh, spouse].filter(notEmpty),
      estMinutes: 18,
      status: hasApproved(hoh.wills_and_trusts) ? 'COMPLETE' : 'NOT_STARTED',
      updatedAt: hoh.wills_and_trusts?.[0]?.updated_at,
      path: paths.dashboard.lwt(hoh.ud_id!),
      primaryRole: 'HOH',                 // 👈 added
      estateDocument: hoh.wills_and_trusts?.[0],
      ud_id: hoh.ud_id ?? '',
      type: 'TRUST',
      joint_trust: true,
      filteredEstateDocs: hoh.wills_and_trusts ?? [],
    });
  } else if (epf === 'skip' || epf === '' || epf === null) {
    tasks.push({
      id: 'skip',
      kind: 'lwt',
      title: 'Choose a Will or Trust',
      people: [hoh, spouse].filter(notEmpty),
      estMinutes: 5,
      status: 'NOT_STARTED',
      updatedAt: hoh.wills_and_trusts?.[0]?.updated_at,
      path: paths.dashboard.lwt(userData?.ud_id!),
      primaryRole: 'HOH',                 // 👈 added
      estateDocument: hoh.wills_and_trusts?.[0],
      ud_id: hoh.ud_id ?? '',
      type: 'WILL',
      joint_trust: false,
      filteredEstateDocs: hoh.wills_and_trusts ?? [],
    });
  } else if (epf === 'attorney') {
    tasks.push({
      id: 'attorney',
      kind: 'lwt',
      title: 'Upload Will or Trust from Attorney',
      people: [hoh, spouse].filter(notEmpty),
      estMinutes: 2,
      status: hasApproved(hoh.wills_and_trusts) ? 'COMPLETE' : 'NOT_STARTED',
      updatedAt: hoh.wills_and_trusts?.[0]?.updated_at,
      path: paths.dashboard.lwt(hoh.ud_id!),
      primaryRole: 'HOH',                 // 👈 added
      estateDocument: hoh.wills_and_trusts?.[0],
      ud_id: hoh.ud_id ?? '',
      type: 'WILL',
      joint_trust: false,
      filteredEstateDocs: hoh.wills_and_trusts ?? [],
    });
  } else if (epf === 'trust' || epf === 'will') {
    const label = epf === 'trust' ? 'Living Trust' : 'Last Will & Testament';

    [hoh, spouse].filter(notEmpty).forEach((p) => {
      const docs = p.wills_and_trusts ?? [];
      const first = p.full_name?.split(/\s+/)[0] ?? 'Member';

      tasks.push({
        id: `${p.ud_id}-lwt`,
        kind: 'lwt',
        title: `${label} for ${first}`,
        type: label === 'Living Trust' ? 'TRUST' : 'WILL',
        people: [p],
        estMinutes: epf === 'trust' ? 14 : 12,
        status: hasApproved(docs) ? 'COMPLETE' : 'NOT_STARTED',
        updatedAt: docs[0]?.updated_at,
        path: paths.dashboard.lwt(p.ud_id!),
        primaryRole: roleOf(p, hoh, spouse),   // 👈 added
        estateDocument: docs[0],
        ud_id: p.ud_id ?? '',
        joint_trust: false,
        filteredEstateDocs: docs,
      });
    });
  }

  // ---- 2. POA + HCD rows for everyone -------------------------------------
  const everyone: UserDataObj[] =
    [hoh, ...members.filter(r => r.is_read_only !== true).map(r => r.role_target_user_data)].filter(notEmpty);

  everyone.forEach((p) => {
    const first = p.full_name?.split(/\s+/)[0] ?? 'Member';
    const r = roleOf(p, hoh, spouse);

    // POA
    tasks.push({
      id: `${p.ud_id}-fpoa`,
      kind: 'fpoa',
      title: `Power of Attorney for ${first}`,
      type: 'FINANCIAL_POWER_OF_ATTORNEY',
      people: [p],
      estMinutes: 5,
      status: hasApproved(p.financial_powers_of_attorney) ? 'COMPLETE' : 'NOT_STARTED',
      updatedAt: p.financial_powers_of_attorney?.[0]?.updated_at,
      path: paths.dashboard.fpoa(p.ud_id!),
      primaryRole: r,                     // 👈 added
      estateDocument: p.financial_powers_of_attorney?.[0],
      ud_id: p.ud_id ?? '',
      joint_trust: false,
      filteredEstateDocs: p.financial_powers_of_attorney ?? [],
    });

    // HCD
    tasks.push({
      id: `${p.ud_id}-hcd`,
      kind: 'hcd',
      title: `Healthcare Directive for ${first}`,
      type: 'HEALTHCARE_DIRECTIVE',
      people: [p],
      estMinutes: 4,
      status: hasApproved(p.healthcare_directives) ? 'COMPLETE' : 'NOT_STARTED',
      updatedAt: p.healthcare_directives?.[0]?.updated_at,
      path: paths.dashboard.hcd(p.ud_id!),
      primaryRole: r,                     // 👈 added
      estateDocument: p.healthcare_directives?.[0],
      ud_id: p.ud_id ?? '',
      joint_trust: false,
      filteredEstateDocs: p.healthcare_directives ?? [],
    });
  });

  return tasks;
};

/* ---------------------------------------------------------------------------
   Sort tasks by (1) person-role priority, then (2) completion status, then
   (3) kind ('lwt', 'fpoa', 'hcd') for a stable, readable list.
--------------------------------------------------------------------------- */

const rolePriority: Record<PersonRole, number> = {
  HOH: 0,
  SPOUSE: 1,
  CHILD: 2,
  OTHER: 3,
};

const statusPriority: Record<Task['status'], number> = {
  NOT_STARTED: 0,
  IN_PROGRESS: 0,
  COMPLETE: 0,
};

const kindOrder: Record<Task['kind'], number> = {  // tie-breaker
  lwt: 0,
  fpoa: 1,
  hcd: 2,
};

const sortTasks = (tasks: TaskPlusRole[]): TaskPlusRole[] =>
  [...tasks].sort((a, b) => {
    // 1️⃣  status: completed items always last
    const byStatus =
      statusPriority[a.status] - statusPriority[b.status]; // 0,1,2
    if (byStatus !== 0) return byStatus;

    // 2️⃣  same status → sort by household role
    const byRole =
      rolePriority[a.primaryRole] - rolePriority[b.primaryRole];
    if (byRole !== 0) return byRole;

    // 3️⃣  tie-breaker: document kind
    return kindOrder[a.kind] - kindOrder[b.kind];
  });


const HouseholdChecklist: FC<{readOnly: boolean}> = ({readOnly}) => {
  // ---------------------------------------------------------------------------
  // 1. source data → flat task array
  // ---------------------------------------------------------------------------
  const { selectedHousehold, refreshHouseholds } = useHousehold();
  const { userData } = useUserDataProvider();
  const estateDocStore = useEstateDocumentStore(null);
  const tasks = useMemo(() => sortTasks(buildTasks(selectedHousehold, userData)), [selectedHousehold, userData]);


  // ---------------------------------------------------------------------------
  // 2. progress bar
  // ---------------------------------------------------------------------------
  const completed = tasks.filter((t) => t.status === 'COMPLETE').length;
  const completionPct = tasks.length
    ? Math.round((completed / tasks.length) * 100)
    : 0;

  // ---------------------------------------------------------------------------
  // 3. context-menu state
  // ---------------------------------------------------------------------------
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuTask, setMenuTask] = useState<Task | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const router = useRouter();


  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuTask(null);
  };

  const downloadAll = useV3DocumentDownloadAll(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_ALL_V3
  );

  const handleDownloadAllDocuments = useCallback(
    async (ud_id: string, estateDocument: MinimalEstateDocument | null) => {
      if (!estateDocument?.id || !ud_id) {
        toast.error("No estate document ID provided");
        return;
      }

      try {
        // Notify the user that the download is starting
        toast.success("Downloading all documents...");
        const params = {
          ud_id: ud_id,
          entity: "estate-documents",
          entity_id: estateDocument.id,
        };
        // Make the request to your Django API
        await downloadAll(params, estateDocument.name || "documents.zip");
      } catch (error) {
        // Handle any errors during the request
        toast.error("Failed to download documents");
        console.error("Download error:", error);
      }
    },
    [downloadAll]
  );

  // Helper function to navigate to the correct document index page for viewing
  const handleViewDocument = useCallback((task: Task) => {
    switch (task.kind) {
      case 'lwt':
        router.push(paths.dashboard.lwt(task.ud_id));
        break;
      case 'fpoa':
        router.push(paths.dashboard.fpoa(task.ud_id));
        break;
      case 'hcd':
        router.push(paths.dashboard.hcd(task.ud_id));
        break;
    }
  }, [router]);

  const createNewEstateDocument = useCallback(async (ud_id: string, full_name: string, type: EstateDocumentType, estateDocs: MinimalEstateDocument[], joint_trust: boolean) => {
    try {

      const filteredEstateDocs = estateDocs.filter((d) => d.type === type);

      setCreatingNew(true);
      let newEstateDoc = null;
      // here we duplicate the most recent document APPROVED document
      if (filteredEstateDocs?.find((d) => d.status === "APPROVED")) {
        newEstateDoc = await estateDocStore.duplicateEstateDocument(
          ud_id || "",
          filteredEstateDocs?.find((d) => d.status === "APPROVED")?.id || ""
        );
      } else {
        newEstateDoc = await estateDocStore.handleAddOrEditEstateDocument(ud_id || "", {
          id: null,
          type: type,
          version: null,
          status: "IN_PROGRESS",
          state_code: null,
          address: null,
          data: null,
          documents: [],
          name: type,
          key_details: null,
          user_name: full_name || "Update Name",
          notes: null,
          document_source: "SNUG",
          joint_trust: joint_trust,
        });
      }
      await refreshHouseholds();
      if (type === 'FINANCIAL_POWER_OF_ATTORNEY') {
        router.replace(
          paths.dashboard.createFinancialPowerOfAttorney(newEstateDoc?.id || "", ud_id || "")
        );
      } else if (type === 'HEALTHCARE_DIRECTIVE') {
        router.replace(
          paths.dashboard.createHealthCareDirective(newEstateDoc?.id || "", ud_id || "")
        );
      } else if (type === 'TRUST' || type === 'WILL') {
        router.replace(
          paths.dashboard.createWillOrTrust(newEstateDoc?.id || "", ud_id || "")
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCreatingNew(false);
    }
  }, [estateDocStore, router, refreshHouseholds]);

  // Helper function to navigate to the correct document index page for updating
  // The index page will handle the logic of creating new vs. continuing existing documents
  const handleUpdateDocument = useCallback(async (task: Task) => {
    // Navigate to the index page - it already has logic to handle document creation/continuation
    let createNew: boolean = false;
    if (
      task.estateDocument?.status === "IN_PROGRESS" &&
      task.estateDocument?.document_source === "SNUG"
    ) {
      createNew = false;
    } else {
      createNew = true;
    }


    if (createNew) {
      await createNewEstateDocument(task.ud_id, "", task.type, task.filteredEstateDocs, task.joint_trust);
      return;
    }

    switch (task.kind) {
      case 'lwt':
        router.push(
            paths.dashboard.createWillOrTrust(
              task.estateDocument?.id || "",
              task.ud_id || ""
            )
        );
        break;
      case 'fpoa':
        router.push(paths.dashboard.fpoa(task.ud_id));
        break;
      case 'hcd':
        router.push(paths.dashboard.hcd(task.ud_id));
        break;
      default:
        router.push(task.path);
    }
  }, [router, createNewEstateDocument]);


  // ---------------------------------------------------------------------------
  // 4. render
  // ---------------------------------------------------------------------------
  return (
    <Card
      variant="outlined"
      sx={{ bgcolor: '#FAF9F8', height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {/* progress bar */}
      <Box sx={{ px: 3, pt: 3 }}>
        <LinearProgress variant="determinate" value={completionPct} />
      </Box>

      <CardHeader title="Essential Documents" />

      <Stack spacing={3} sx={{ px: 3, pb: 3 }}>
        <Typography variant="body2">
          A comprehensive estate plan includes a short list of essential documents. Most
          families can create their documents in under 30&nbsp;minutes.
        </Typography>

        <Card variant="outlined">
          <List disablePadding>
            {tasks.map((task) => (
              <>
                <ChecklistRow
                  readOnly={readOnly}
                  key={task.id}
                  task={task}
                  // ⬇︎ accept BOTH id and anchor
                  onMenu={(id: string, anchor: HTMLElement) => {
                    const found = tasks.find((t) => t.id === id);
                    if (found) {
                      setMenuTask(found);
                      setAnchorEl(anchor);                    // 🔄 use THIS
                    }
                  }}
                />
                <Divider />
              </>
            ))}
          </List>
        </Card>
      </Stack>

      {/* context-menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          [`& .${menuItemClasses.root}`]: {
            fontSize: 14,
            '& svg': { mr: 1 },
          },
        }}
      >
        <MenuItem
          disabled={creatingNew}
          onClick={() => {
            if (menuTask) handleViewDocument(menuTask);
            handleCloseMenu();
          }}
        >
          <SvgIcon fontSize="small">
            <ArrowOutwardOutlinedIcon />
          </SvgIcon>
          View
        </MenuItem>

        <MenuItem
          disabled={creatingNew}
          onClick={() => {
            
            if (menuTask) {
              handleDownloadAllDocuments(menuTask.ud_id, menuTask.estateDocument ?? null);
            }
            handleCloseMenu();
          }}
        >
          <SvgIcon fontSize="small">
            <FileDownloadOutlinedIcon />
          </SvgIcon>
          Download
        </MenuItem>

        {creatingNew ? (
          <MenuItem disabled>
            <SvgIcon fontSize="small">
              <EditNoteOutlinedIcon />
            </SvgIcon>
            <CircularProgress size={18} sx={{ ml: 1, mr: 1 }} />
            Updating...
          </MenuItem>
        ) : (
          <MenuItem
            disabled={creatingNew}
            onClick={() => {
              if (menuTask) {
                handleUpdateDocument(menuTask);
              }
              handleCloseMenu();
            }}
          >
            <SvgIcon fontSize="small">
              <EditNoteOutlinedIcon />
            </SvgIcon>
            Update
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};

export { HouseholdChecklist };